import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ReferenceScheme, ViewMode } from 'src/app/reference-scheme/models/reference-scheme.model';

import { AdditiveRow, FeedAdvice } from '../../../feed-advice/models/feed-advice.model';
import { Product } from '../../models';

@Component({
  selector: 'app-additive',
  templateUrl: './additive.component.html',
  standalone: false,
})
export class AdditiveComponent implements OnInit, AfterViewInit {
  @Input() viewMode: ViewMode;
  @Input() isReferenceFeedAdvice = false;
  @Input() feedAdvice: FeedAdvice;
  @Input() referenceFeedAdvice: ReferenceScheme;
  @Input() additives$: Observable<Product[]>;
  @Output() additiveChanged: EventEmitter<AdditiveRow[]> = new EventEmitter<AdditiveRow[]>();
  @Output() showAdditiveChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  showAdditives = true;
  public viewModes: typeof ViewMode = ViewMode;

  /* ADDITIVES */
  dosageControls = new FormArray([]);
  applicationMomentControls = new FormArray([]);
  commentControls = new FormArray([]);
  productControls = new FormArray([]);

  /* TEMPLATES */
  @ViewChild('nameTemplate') additiveNameTemplate: TemplateRef<Product>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as AdditiveRow[]),
    selectColumn: false,
    pagination: false,
    columns: [],
  };

  constructor(
    private readonly store: Store<RootState>,
    @Inject(LocaleService) private locale: LocaleService,
    private changeDectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.viewMode === ViewMode.View) {
      this.tableConfig = {
        dataSource: new MatTableDataSource([] as AdditiveRow[]),
        selectColumn: false,
        pagination: false,
        columns: [
          {
            id: 'productName',
            type: TableColumnTypes.TEMPLATE,
            label: this.locale.translate('feed-advice.create.table-header.productName'),
          },
          {
            id: 'dosage',
            type: TableColumnTypes.NUMBER,
            label: this.locale.translate('feed-advice.create.table-header.dosage'),
          },
          {
            id: 'applicationMoment',
            type: TableColumnTypes.STRING,
            label: this.locale.translate('feed-advice.create.table-header.applicationMoment'),
          },
          {
            id: 'comment',
            type: TableColumnTypes.STRING,
            label: this.locale.translate('feed-advice.create.table-header.comment'),
          },
        ],
        scrolling: true,
        width: '1200px',
        wrapperWidth: '100%',
        dragDrop: false,
      };
    } else {
      this.tableConfig = {
        dataSource: new MatTableDataSource([] as AdditiveRow[]),
        selectColumn: false,
        pagination: false,
        columns: [
          {
            id: 'productName',
            type: TableColumnTypes.TEMPLATE,
            label: this.locale.translate('feed-advice.create.table-header.productName'),
            inputConfig: {
              formArray: this.productControls,
              options: [Validators.required],
            },
          },
          {
            id: 'dosage',
            type: TableColumnTypes.INPUT_NUMBER,
            inputConfig: {
              step: 0.01,
              formArray: this.dosageControls,
              options: [Validators.required],
            },
            label: this.locale.translate('feed-advice.create.table-header.dosage'),
          },
          {
            id: 'applicationMoment',
            type: TableColumnTypes.INPUT_STRING,
            inputConfig: {
              formArray: this.applicationMomentControls,
            },
            label: this.locale.translate('feed-advice.create.table-header.applicationMoment'),
          },
          {
            id: 'comment',
            type: TableColumnTypes.INPUT_STRING,
            inputConfig: {
              formArray: this.commentControls,
            },
            label: this.locale.translate('feed-advice.create.table-header.comment'),
          },
        ],
        actions: [
          {
            id: 'delete',
            label: '',
            callback: (row: AdditiveRow) => this.deleteAdditiveRow(row),
            icon: 'delete',
            disabled: () => false,
          },
        ],
        scrolling: true,
        width: '1200px',
        wrapperWidth: '100%',
        dragDrop: true,
        dragDropped: (event, row: AdditiveRow) => this.dragDropAdditiveRow(event, row),
        enableArrowKeys: true,
      };
    }

    if (!this.isReferenceFeedAdvice) {
      if (this.feedAdvice !== null && this.feedAdvice !== undefined) {
        if (this.feedAdvice.additiveRows?.length > 0) {
          this.showAdditives = true;
        } else {
          this.showAdditives = false;
        }

        if (this.feedAdvice.additiveRows !== undefined) {
          this.tableConfig.dataSource.data = this.feedAdvice.additiveRows
            ?.map((a) => {
              const row = Object.assign({}, a);
              return row;
            })
            .sort((a, b) => (a.id > b.id ? 1 : -1));

          if (
            this.viewMode === ViewMode.View &&
            !this.feedAdvice.additiveRows.some((row) => !!row.comment && row.comment.length > 0)
          ) {
            this.tableConfig.columns = this.tableConfig.columns.filter((c) => c.id !== 'comment');
          }

          this.additiveChanged.emit(this.feedAdvice.additiveRows);
          this.showAdditiveChanged.emit(this.showAdditives);
        } else {
          this.tableConfig.dataSource.data = [
            {
              id: 0,
              product: null,
              dosage: null,
              applicationMoment: null,
              comment: null,
            },
          ];
        }
      } else {
        this.tableConfig.dataSource.data = [
          {
            id: 0,
            product: null,
            dosage: null,
            applicationMoment: null,
            comment: null,
          },
        ];
      }
    } else {
      if (this.referenceFeedAdvice !== null && this.referenceFeedAdvice !== undefined) {
        if (this.referenceFeedAdvice.additiveRows?.length > 0) {
          this.showAdditives = true;
        } else {
          this.showAdditives = false;
        }

        if (this.referenceFeedAdvice.additiveRows !== undefined) {
          this.tableConfig.dataSource.data = this.referenceFeedAdvice.additiveRows
            ?.map((a) => {
              const row = Object.assign({}, a);
              return row;
            })
            .sort((a, b) => (a.id > b.id ? 1 : -1));

          if (
            this.viewMode === ViewMode.View &&
            !this.referenceFeedAdvice.additiveRows.some(
              (row) => !!row.comment && row.comment.length > 0,
            )
          ) {
            this.tableConfig.columns = this.tableConfig.columns.filter((c) => c.id !== 'comment');
          }

          this.additiveChanged.emit(this.referenceFeedAdvice.additiveRows);
          this.showAdditiveChanged.emit(this.showAdditives);
        } else {
          this.tableConfig.dataSource.data = [
            {
              id: 0,
              product: null,
              dosage: null,
              applicationMoment: null,
              comment: null,
            },
          ];
        }
      } else {
        this.tableConfig.dataSource.data = [
          {
            id: 0,
            product: null,
            dosage: null,
            applicationMoment: null,
            comment: null,
          },
        ];
      }
    }
  }

  ngAfterViewInit() {
    const newAdditiveConfig = { ...this.tableConfig };
    const columnAdditiveName = newAdditiveConfig.columns.find((c) => c.id === 'productName');

    columnAdditiveName.template = this.additiveNameTemplate;
    this.tableConfig = newAdditiveConfig;

    this.changeDectorRef.detectChanges();
  }

  selectAdditive(selection: MatSelectChange, additiveRow: AdditiveRow) {
    this.productControls.controls[additiveRow.id].setValue(<Product>selection.value);
    additiveRow = {
      ...additiveRow,
      product: this.productControls.controls[additiveRow.id].value as Product,
      dosage: this.dosageControls.controls[additiveRow.id].value as number,
      applicationMoment: this.applicationMomentControls.controls[additiveRow.id].value as string,
      comment: this.commentControls.controls[additiveRow.id].value as string,
    };

    this.updateDataSource();
  }

  getAdditive(row: AdditiveRow): Product {
    let foundAdditive = <Product>{};
    if (row.product) {
      this.additives$.pipe(take(1)).subscribe((additives) => {
        foundAdditive =
          !!additives && additives.find((product) => product.code === row.product.code);
      });
    }
    const productControl = new FormControl<Product>(null);
    productControl.setValue(foundAdditive);

    if (
      this.productControls.controls[row.id] === null ||
      this.productControls.controls[row.id] === undefined
    ) {
      this.productControls.setControl(row.id, productControl);
    } else {
      this.productControls.removeAt(row.id);
      this.productControls.setControl(row.id, productControl);
    }

    return foundAdditive;
  }

  addAdditiveRow() {
    this.updateDataSource();
    this.tableConfig.dataSource.data = [
      ...this.tableConfig.dataSource.data,
      {
        id:
          this.tableConfig.dataSource.data.length > 0
            ? Math.max(...this.tableConfig.dataSource.data.map((row: AdditiveRow) => row.id)) + 1
            : 0,
        product: null,
        dosage: null,
        applicationMoment: null,
        comment: null,
      },
    ];
  }

  deleteAdditiveRow(additiveRow: AdditiveRow) {
    const additiveList = <AdditiveRow[]>[];
    let index = 0;
    // After filtering selected row out, the remaining rows get new ids starting with 0
    // because otherwise problems will arise if the user should start drag/dropping rows.
    [
      ...this.tableConfig.dataSource.data.filter((row: AdditiveRow) => row.id !== additiveRow.id),
    ].forEach((row: AdditiveRow) => {
      const newRow = <AdditiveRow>{
        ...row,
        id: index,
      };
      additiveList.push(newRow);
      index = index + 1;
    });
    this.tableConfig.dataSource.data = additiveList.sort((a: AdditiveRow, b: AdditiveRow) =>
      a.id > b.id ? 1 : -1,
    );

    this.updateDataSource();
  }

  dragDropAdditiveRow(event: CdkDragDrop<unknown>, additiveRow: AdditiveRow) {
    this.updateDataSource();
    // Fill product row with details from formArrays
    additiveRow = {
      ...additiveRow,
      dosage: this.dosageControls.controls[additiveRow.id].value as number,
      applicationMoment: this.applicationMomentControls.controls[additiveRow.id].value as string,
      comment: this.commentControls.controls[additiveRow.id].value as string,
    };

    const additiveList = <AdditiveRow[]>[];
    let index = 0;
    const filtered = this.tableConfig.dataSource.data.filter(
      (row: AdditiveRow) => row.id !== event.previousIndex,
    );
    const draggedRow = <AdditiveRow>{ ...additiveRow, id: event.currentIndex };
    // the draggedRow is put in its selected place in the array
    filtered.splice(event.currentIndex, 0, draggedRow);
    // the whole array gets new ids if needed, so that they are in ascending order with no doubles
    filtered.forEach((row: AdditiveRow) => {
      const newRow = <AdditiveRow>{
        ...row,
        id: index,
      };
      additiveList.push(newRow);
      index = index + 1;
    });
    this.tableConfig.dataSource.data = additiveList.sort((a: AdditiveRow, b: AdditiveRow) =>
      a.id > b.id ? 1 : -1,
    );
  }

  updateDataSource() {
    const additiveRows = this.tableConfig.dataSource.data.map((a: AdditiveRow) =>
      Object.assign({}, a),
    );

    additiveRows.forEach((row) => {
      row.product = this.productControls.controls[row.id].value as Product;
      row.dosage = this.dosageControls.controls[row.id].value as number;
      row.applicationMoment = this.applicationMomentControls.controls[row.id].value as string;
      row.comment = this.commentControls.controls[row.id].value as string;
    });

    this.tableConfig.dataSource.data = additiveRows;

    this.additiveChanged.emit(additiveRows);
    this.showAdditiveChanged.emit(this.showAdditives);
  }

  setShowAdditives(showAdditives: boolean) {
    this.showAdditives = showAdditives;
    this.showAdditiveChanged.emit(this.showAdditives);
  }
}
